import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { recurringApi, templatesApi } from '@/api/templates'
import { AppShell } from '@/components/AppShell'
import { toast } from '@/lib/toast'
import { IconRepeat, IconTrash } from '@/components/icons'
import { Alert, Button, EmptyState, Field, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { WeekdayPicker } from '@/features/delivery/DeliveryWindow'
import { formatDate } from '@/lib/format'
import { formatQuantity } from '@/features/orders/status'

/** Πρότυπα παραγγελιών (§11, οθόνη 8). */
export default function TemplatesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [scheduling, setScheduling] = useState<string | null>(null)

  const templates = useQuery({ queryKey: ['templates'], queryFn: templatesApi.list })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['templates'] })

  const saveFromCart = useMutation({
    mutationFn: () => templatesApi.fromCart(name.trim()),
    onSuccess: () => {
      setName('')
      setSaving(false)
      void invalidate()
      toast.success(t('templates.saved'))
    },
  })
  const load = useMutation({
    mutationFn: templatesApi.loadIntoCart,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      toast.success(t('templates.loaded'))
      navigate('/cart')
    },
  })
  const remove = useMutation({
    mutationFn: templatesApi.remove,
    onSuccess: () => {
      void invalidate()
      toast.success(t('templates.removed'))
    },
  })

  const schedules = useQuery({ queryKey: ['recurring-orders'], queryFn: recurringApi.list })
  const invalidateSchedules = () =>
    queryClient.invalidateQueries({ queryKey: ['recurring-orders'] })

  const toggleSchedule = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      recurringApi.update(id, { isActive }),
    onSuccess: () => void invalidateSchedules(),
  })
  const removeSchedule = useMutation({
    mutationFn: recurringApi.remove,
    onSuccess: () => void invalidateSchedules(),
  })

  return (
    <AppShell
      title={t('templates.title')}
      action={!saving ? { label: t('templates.saveCart'), onClick: () => setSaving(true) } : null}
    >
      {saving && (
        <form
          className="mb-4 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim()) saveFromCart.mutate()
          }}
        >
          <h2 className="font-semibold text-gray-900">{t('templates.saveCart')}</h2>
          <p className="text-sm text-gray-500">{t('templates.saveCartHint')}</p>
          {saveFromCart.isError && <Alert>{translateError(saveFromCart.error)}</Alert>}

          <Field
            label={t('templates.name')}
            name="templateName"
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <div className="flex gap-2">
            <Button type="submit" loading={saveFromCart.isPending} disabled={!name.trim()}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setSaving(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}

      {templates.isPending && <SkeletonList rows={3} />}
      {templates.data?.length === 0 && (
        <EmptyState
          icon={<IconRepeat size={26} />}
          title={t('templates.empty')}
          description={t('templates.emptyHint')}
        />
      )}

      <ul className="flex flex-col gap-2">
        {templates.data?.map((template) => (
          <li key={template.id} className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <button
                onClick={() => setExpanded(expanded === template.id ? null : template.id)}
                className="min-w-0 flex-1 text-left"
              >
                <p className="truncate font-semibold text-gray-900">{template.name}</p>
                <p className="text-xs text-gray-500">
                  {t('templates.itemCount', { count: template.itemCount })} ·{' '}
                  {t('templates.supplierCount', { count: template.supplierCount })}
                </p>
              </button>
              <button
                aria-label={t('common.delete')}
                onClick={() => remove.mutate(template.id)}
                className="shrink-0 rounded-lg px-2 py-2 text-gray-400 transition-colors hover:text-red-600"
              >
                <IconTrash size={18} />
              </button>
            </div>

            {expanded === template.id && (
              <ul className="mt-3 divide-y divide-gray-100 border-t border-gray-100 pt-2">
                {template.items.map((item) => (
                  <li key={item.id} className="flex justify-between gap-3 py-2 text-sm">
                    <span className="min-w-0 truncate">
                      {item.name}
                      <span className="ml-2 text-xs text-gray-400">{item.supplierName}</span>
                    </span>
                    <span className="shrink-0 text-gray-600">
                      {formatQuantity(item.quantity)} {t(`units.${item.unit}`)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {(() => {
              const schedule = schedules.data?.find((s) => s.templateId === template.id)
              if (!schedule) return null
              return (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-brand-50 px-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-brand-900">
                      {schedule.weekdays.map((d) => t(`weekdaysShort.${d}`)).join(' ')} ·{' '}
                      {schedule.runAt.slice(0, 5)}
                    </span>
                    <span className="text-xs text-brand-800">
                      {schedule.isActive && schedule.nextRunDate
                        ? t('recurring.nextRun', { date: formatDate(schedule.nextRunDate) })
                        : t('recurring.paused')}
                    </span>
                  </span>
                  <span className="flex shrink-0 gap-1">
                    <button
                      onClick={() =>
                        toggleSchedule.mutate({ id: schedule.id, isActive: !schedule.isActive })
                      }
                      className="rounded-lg px-2 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                    >
                      {schedule.isActive ? t('recurring.pause') : t('recurring.resume')}
                    </button>
                    <button
                      aria-label={t('common.delete')}
                      onClick={() => removeSchedule.mutate(schedule.id)}
                      className="rounded-lg px-2 py-1.5 text-gray-400 transition-colors hover:text-red-600"
                    >
                      <IconTrash size={16} />
                    </button>
                  </span>
                </div>
              )
            })()}

            {scheduling === template.id && (
              <ScheduleForm
                templateId={template.id}
                onDone={() => {
                  setScheduling(null)
                  void invalidateSchedules()
                }}
              />
            )}

            <div className="mt-3 flex flex-col gap-2">
              <Button
                loading={load.isPending && load.variables === template.id}
                onClick={() => load.mutate(template.id)}
              >
                {t('templates.load')}
              </Button>
              {!schedules.data?.some((s) => s.templateId === template.id) &&
                scheduling !== template.id && (
                  <Button variant="secondary" onClick={() => setScheduling(template.id)}>
                    {t('recurring.add')}
                  </Button>
                )}
            </div>
          </li>
        ))}
      </ul>
    </AppShell>
  )
}

/**
 * Ορισμός αυτόματης συμπλήρωσης.
 *
 * Ο τίτλος λέει ρητά «συμπλήρωση καλαθιού» και όχι «αυτόματη παραγγελία»: το
 * σύστημα δεν στέλνει τίποτα μόνο του, και ο χρήστης πρέπει να το ξέρει πριν
 * το ενεργοποιήσει, όχι να το ανακαλύψει την πρώτη Δευτέρα.
 */
function ScheduleForm({ templateId, onDone }: { templateId: string; onDone: () => void }) {
  const { t } = useTranslation()
  const translateError = useApiError()
  const [days, setDays] = useState<number[]>([])
  const [time, setTime] = useState('07:00')

  const create = useMutation({
    mutationFn: () =>
      recurringApi.create({ templateId, weekdays: days, runAt: `${time}:00` }),
    onSuccess: onDone,
  })

  return (
    <form
      className="mt-3 flex flex-col gap-3 rounded-xl border border-gray-200 p-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (days.length > 0) create.mutate()
      }}
    >
      <h3 className="font-semibold text-gray-900">{t('recurring.title')}</h3>
      <p className="text-xs text-gray-500">{t('recurring.hint')}</p>

      {create.isError && <Alert>{translateError(create.error)}</Alert>}

      <div className="flex flex-col gap-2 text-sm font-medium text-gray-700">
        {t('recurring.days')}
        <WeekdayPicker value={days} onChange={setDays} />
        {days.length === 0 && (
          <span className="text-xs font-normal text-gray-500">{t('recurring.chooseDays')}</span>
        )}
      </div>

      <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
        {t('recurring.time')}
        <input
          type="time"
          step={3600}
          value={time}
          onChange={(event) => setTime(event.target.value)}
          className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-gray-900"
        />
      </label>

      <div className="flex gap-2">
        <Button type="submit" loading={create.isPending} disabled={days.length === 0}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
